import { Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Badge, Button, Card, CardContent, Dialog, DialogContent, Field, Input, PageHeader, Select, Textarea, useToast } from '@marketingbee/ui';
import { FeatureGate, QueryView } from '../components/common';
import { errorMessage, trpc } from '../lib/trpc';

type Step = { channel: 'email' | 'task'; delayMinutes: number; subject: string; body: string };

export function SequencesPage() {
  return (
    <FeatureGate feature="email_marketing">
      <Sequences />
    </FeatureGate>
  );
}

function Sequences() {
  const list = trpc.sequences.list.useQuery();
  const update = trpc.sequences.update.useMutation();
  const runDue = trpc.sequences.runDue.useMutation();
  const utils = trpc.useUtils();
  const { success } = useToast();
  const [open, setOpen] = useState(false);
  const [enrollFor, setEnrollFor] = useState<string | null>(null);
  return (
    <div>
      <PageHeader
        title="Sequences"
        description="Multi-step email cadences with task reminders for your team. They stop on a reply or an unsubscribe, and emails only go to contacts with consent."
        actions={
          <>
            <Button variant="outline" loading={runDue.isPending} onClick={() => runDue.mutate(undefined, { onSuccess: async (r) => { success(`Ran ${r.ran} of ${r.due} due steps`); await utils.sequences.list.invalidate(); } })}>Run due steps</Button>
            <Button onClick={() => setOpen(true)}><Plus /> New sequence</Button>
          </>
        }
      />
      <QueryView query={list}>
        {(data) => (
          <div className="grid gap-4 md:grid-cols-2">
            {data.map((s) => (
              <Card key={s.id}>
                <CardContent className="space-y-3 py-5">
                  <div className="flex items-center justify-between">
                    <h3 className="text-[16px] font-semibold">{s.name}</h3>
                    <Badge variant={s.status === 'active' ? 'success' : 'outline'}>{s.status}</Badge>
                  </div>
                  <p className="text-[14px] text-muted-foreground">{s.description ?? ''}</p>
                  <p className="font-mono text-[13px]">active {s.enrollments.active ?? 0} · completed {s.enrollments.completed ?? 0} · stopped {s.enrollments.stopped ?? 0}</p>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => update.mutate({ id: s.id, status: s.status === 'active' ? 'paused' : 'active' }, { onSuccess: () => void utils.sequences.list.invalidate() })}>{s.status === 'active' ? 'Pause' : 'Activate'}</Button>
                    <Button size="sm" variant="outline" onClick={() => setEnrollFor(s.id)}>Enroll a list</Button>
                  </div>
                </CardContent>
              </Card>
            ))}
            {data.length === 0 ? <p className="text-muted-foreground">No sequences yet.</p> : null}
          </div>
        )}
      </QueryView>
      <NewSequence open={open} onOpenChange={setOpen} />
      <EnrollDialog sequenceId={enrollFor} onClose={() => setEnrollFor(null)} />
    </div>
  );
}

function NewSequence({ open, onOpenChange }: { open: boolean; onOpenChange: (v: boolean) => void }) {
  const create = trpc.sequences.create.useMutation();
  const utils = trpc.useUtils();
  const [name, setName] = useState('');
  const [steps, setSteps] = useState<Step[]>([
    { channel: 'email', delayMinutes: 0, subject: 'Welcome, {{firstName}}', body: 'Hi {{firstName}}, thanks for signing up. {{business}}' },
    { channel: 'task', delayMinutes: 2880, subject: '', body: 'Check whether {{firstName}} opened the welcome email and follow up.' },
  ]);
  const patch = (i: number, p: Partial<Step>) => setSteps((s) => s.map((x, j) => (j === i ? { ...x, ...p } : x)));
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent title="New sequence" size="lg">
        <div className="space-y-4">
          {create.error ? <p className="text-sm text-destructive">{errorMessage(create.error)}</p> : null}
          <Field label="Name"><Input value={name} onChange={(e) => setName(e.target.value)} /></Field>
          {steps.map((s, i) => (
            <Card key={i}>
              <CardContent className="grid gap-3 py-4 sm:grid-cols-3">
                <Field label={`Step ${i + 1}`}><Select value={s.channel} onChange={(e) => patch(i, { channel: e.target.value as Step['channel'] })} options={[{ value: 'email', label: 'Email' }, { value: 'task', label: 'Task for the team' }]} /></Field>
                <Field label="Wait (hours)"><Input type="number" min={0} value={s.delayMinutes / 60} onChange={(e) => patch(i, { delayMinutes: Math.round(Number(e.target.value) * 60) })} /></Field>
                <div className="flex items-end justify-end"><Button variant="ghost" size="sm" aria-label="Remove step" onClick={() => setSteps((x) => x.filter((_, j) => j !== i))}><Trash2 /></Button></div>
                {s.channel === 'email' ? <Field label="Subject" className="sm:col-span-3"><Input value={s.subject} onChange={(e) => patch(i, { subject: e.target.value })} /></Field> : null}
                <Field label={s.channel === 'task' ? 'Task' : 'Message'} className="sm:col-span-3"><Textarea rows={2} value={s.body} onChange={(e) => patch(i, { body: e.target.value })} /></Field>
              </CardContent>
            </Card>
          ))}
          <div className="flex justify-between">
            <Button variant="outline" onClick={() => setSteps((s) => [...s, { channel: 'email', delayMinutes: 2880, subject: 'A quick tip, {{firstName}}', body: 'Hi {{firstName}}, here is one thing that helps.' }])}><Plus /> Add step</Button>
            <Button disabled={!name.trim() || steps.length === 0} loading={create.isPending} onClick={() => create.mutate({ name, stopOnReply: true, stopOnBooked: true, stopOnOptOut: true, steps: steps.map((s) => ({ ...s, subject: s.subject || null })) }, { onSuccess: async () => { onOpenChange(false); setName(''); await utils.sequences.list.invalidate(); } })}>Create</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function EnrollDialog({ sequenceId, onClose }: { sequenceId: string | null; onClose: () => void }) {
  const lists = trpc.crm.lists.list.useQuery(undefined, { enabled: !!sequenceId });
  const [listId, setListId] = useState('');
  const list = trpc.crm.lists.get.useQuery({ id: listId }, { enabled: !!listId });
  const enroll = trpc.sequences.enroll.useMutation();
  const utils = trpc.useUtils();
  const { success } = useToast();
  return (
    <Dialog open={!!sequenceId} onOpenChange={(v) => !v && onClose()}>
      <DialogContent title="Enroll contacts">
        <div className="space-y-4">
          <Field label="List or segment"><Select value={listId} onChange={(e) => setListId(e.target.value)} placeholder="Choose" options={(lists.data ?? []).map((l) => ({ value: l.id, label: `${l.name} (${l.size})` }))} /></Field>
          <Button
            disabled={!list.data?.contacts.length}
            loading={enroll.isPending}
            onClick={() => sequenceId && enroll.mutate({ sequenceId, contactIds: list.data!.contacts.slice(0, 500).map((c) => c.id) }, { onSuccess: async (r) => { success(`Enrolled ${r.enrolled}`, r.skipped ? `${r.skipped} skipped (already enrolled or opted out)` : undefined); onClose(); await utils.sequences.list.invalidate(); } })}
          >
            Enroll {list.data?.contacts.length ?? ''} contacts
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
